import type { ColumnLayoutController } from '@/lib/use-column-layout';

/**
 * WHAT A READER HAS HIDDEN IN A REVENUE MATRIX — the pure half (owner,
 * 2026-10-27: "hide years", "hide sub products", "both ways in both panels").
 * The stateful half is `use-matrix-visibility.ts`; the maths over what is left
 * is `projectMatrix` in `revenue-matrix.ts`. One copy of each rule, used by the
 * Revenue by Company and Revenue by Product panels alike.
 *
 * ── WHAT IS STORED ────────────────────────────────────────────────────────
 *
 * `preferences.columns.<scope>.hidden` — HIDDEN ids only, the house convention
 * (`use-column-layout`): anything a matrix gains later is visible by default,
 * and a stored preference can only ever subtract. One scope per panel ×
 * dimension, so hiding a year applies to every company (or product) expanded
 * in that panel, and the two panels never share a choice.
 *
 * ⛔ **THE SCOPE STRINGS AND THE ID SPELLINGS ARE STORAGE KEYS — NEVER RENAME
 * THEM.** A rename silently un-hides every reader's choices.
 *
 * | id | names |
 * | --- | --- |
 * | `cycle:<cycleId>` | a year column |
 * | the matrix row key — `product:<id>`, `company:<id>`, `uncatalogued:…` | a row |
 * | `flag:hide-add-ons` | the "Hide add-ons" switch (products scope only) |
 *
 * ⚠️ **A STORED ID THE MATRIX DOES NOT RENDER IS IGNORED, NEVER SHOWN AND
 * NEVER DROPPED.** Company A's years differ from company B's; hiding 2023 on A
 * must survive opening B, which has no 2023.
 *
 * ── THE ADD-ONS SWITCH IS INDEPENDENT OF THE PER-ROW CHOICES ──────────────
 *
 * It is stored as its own id, never by writing every add-on's row id into the
 * hidden set — so switching it off restores exactly the rows the reader had
 * hidden themselves, and it reaches add-ons in companies not opened yet. Which
 * products are add-ons is the catalogue FLAG (`Product.isAddOn`), ⛔ never a
 * name containing "Add-ons".
 */

/** One scope per panel × dimension. ⛔ Never rename — see the header. */
export const MATRIX_VISIBILITY_SCOPES = {
  companyMatrixCycles: 'revenue-company.matrix.cycles',
  companyMatrixProducts: 'revenue-company.matrix.products',
  productMatrixCycles: 'revenue-product.matrix.cycles',
  productMatrixCompanies: 'revenue-product.matrix.companies',
} as const;

/** The stored id of the "Hide add-ons" switch. ⛔ Never rename. */
export const HIDE_ADD_ONS_ID = 'flag:hide-add-ons';

export const HIDE_ADD_ONS_LABEL = 'Hide add-ons';

/** The hidden id of a year column. ⛔ Never rename. */
export function cycleColumnKey(cycleId: string): `cycle:${string}` {
  return `cycle:${cycleId}`;
}

/** True when a row key is held back by the add-ons switch (not by the reader's own tick). */
export function hiddenByAddOnSwitch(
  key: string,
  hidden: ReadonlySet<string>,
  addOnRowKeys: ReadonlySet<string> | null | undefined,
): boolean {
  return addOnRowKeys != null && hidden.has(HIDE_ADD_ONS_ID) && addOnRowKeys.has(key);
}

/** Whether a row (or column) key is shown: not hidden by the reader, not held back by the switch. */
export function isKeyShown(
  key: string,
  hidden: ReadonlySet<string>,
  addOnRowKeys?: ReadonlySet<string> | null,
): boolean {
  return !hidden.has(key) && !hiddenByAddOnSwitch(key, hidden, addOnRowKeys);
}

/**
 * A `ColumnLayoutController` over one matrix's live keys, so the shared
 * `ColumnManager` renders the picker. Reorder and freeze are not offered
 * (`reorderable={false}` / `freezable={false}` on the manager), so their verbs
 * here do nothing.
 *
 * ⚠️ **A row held back by the add-ons switch is reported LOCKED and hidden**, so
 * its tick is disabled with the reason beside it rather than silently ignored;
 * the reader's own choice for it is kept underneath and returns when the switch
 * goes off.
 *
 * ⚠️ **Hiding the last visible key is refused**, matching the manager's own
 * disabled tick. A matrix can still end up with nothing visible — a year hidden
 * in another company, or the add-ons switch — and the table answers that with
 * an empty state and a "Show all" action rather than a blank grid.
 */
export function pickerController({
  liveKeys,
  hidden,
  loading,
  addOnRowKeys,
  setHidden,
}: {
  liveKeys: readonly string[];
  hidden: ReadonlySet<string>;
  loading: boolean;
  addOnRowKeys?: ReadonlySet<string> | null;
  setHidden: (keys: readonly string[], hide: boolean) => void;
}): ColumnLayoutController {
  const isVisible = (key: string) => isKeyShown(key, hidden, addOnRowKeys);
  const visibleKeys = liveKeys.filter(isVisible);
  const noop = () => {};
  return {
    visibleKeys,
    orderedKeys: liveKeys,
    isVisible,
    frozenSideOf: () => undefined,
    toggleVisible: (key) => {
      if (hiddenByAddOnSwitch(key, hidden, addOnRowKeys)) return;
      const hide = !hidden.has(key);
      if (hide && visibleKeys.length === 1 && visibleKeys[0] === key) return;
      setHidden([key], hide);
    },
    showAll: () => setHidden(liveKeys, false),
    setFrozen: noop,
    move: noop,
    moveTo: noop,
    // Reset is the default: every live key shown and the switch off.
    reset: () => setHidden([...liveKeys, HIDE_ADD_ONS_ID], false),
    hiddenCount: liveKeys.length - visibleKeys.length,
    isDefault: liveKeys.every((key) => !hidden.has(key)) && !hidden.has(HIDE_ADD_ONS_ID),
    loading,
    pinnedLeftKeys: [],
  };
}
